/**
 * WidgetSession — persistence for conversation + panel state.
 *
 * conversation_id lives in localStorage (survives reload + accidental tab
 * close within TTL) so the user picks up where they left off. The panel
 * open/closed flag lives in sessionStorage — it should follow the visitor
 * across page navigations within one browsing session (tab) but a freshly
 * opened tab starts closed. session_token is NOT persisted (short TTL ~15min
 * — re-bootstrap is cheaper than rotating it client-side).
 *
 * Storage keys scoped by widget_key (prefix is brand-derived from BRAND_SLUG):
 *  - {brand}:{widget_key}:conversation_id  (localStorage, string)
 *  - {brand}:{widget_key}:expires_at_ms    (localStorage, Unix ms)
 *  - {brand}:{widget_key}:panel_open       (sessionStorage, '1' | '0')
 *  - {brand}:{widget_key}:nudge_dismissed:{id} (sessionStorage, '1')
 *
 * TTL: 24h (matches docs/11 conversation_hard_close_seconds = 86400).
 *
 * SSR-safe: storage probe at construction; per-call ops no-op when unavailable.
 */

import { BRAND_SLUG } from '../brand.js';

const TTL_MS = 24 * 60 * 60 * 1000;

function probeStorage(kind: 'local' | 'session'): Storage | null {
  try {
    if (typeof window === 'undefined') {
      return null;
    }
    const storage = kind === 'local' ? window.localStorage : window.sessionStorage;
    if (!storage) {
      return null;
    }
    const probe = `__${BRAND_SLUG}_probe__`;
    storage.setItem(probe, '1');
    storage.removeItem(probe);
    return storage;
  } catch {
    return null;
  }
}

export class WidgetSession {
  private readonly storage: Storage | null;

  private readonly sessionStore: Storage | null;

  private readonly conversationKey: string;

  private readonly expiresKey: string;

  private readonly panelOpenKey: string;

  private readonly peekDismissedKey: string;

  private readonly nudgeDismissedPrefix: string;

  constructor(widgetKey: string) {
    this.storage = probeStorage('local');
    this.sessionStore = probeStorage('session');
    this.conversationKey = `${BRAND_SLUG}:${widgetKey}:conversation_id`;
    this.expiresKey = `${BRAND_SLUG}:${widgetKey}:expires_at_ms`;
    this.panelOpenKey = `${BRAND_SLUG}:${widgetKey}:panel_open`;
    this.peekDismissedKey = `${BRAND_SLUG}:${widgetKey}:peek_dismissed`;
    this.nudgeDismissedPrefix = `${BRAND_SLUG}:${widgetKey}:nudge_dismissed:`;
  }

  getConversationId(): string | null {
    if (!this.storage) return null;

    const expiresStr = this.storage.getItem(this.expiresKey);
    if (expiresStr !== null) {
      const expires = Number.parseInt(expiresStr, 10);
      if (!Number.isFinite(expires) || expires < Date.now()) {
        this.clear();
        return null;
      }
    }

    return this.storage.getItem(this.conversationKey);
  }

  saveConversationId(conversationId: string): void {
    if (!this.storage) return;

    this.storage.setItem(this.conversationKey, conversationId);
    this.storage.setItem(this.expiresKey, String(Date.now() + TTL_MS));
  }

  clear(): void {
    if (!this.storage) return;

    this.storage.removeItem(this.conversationKey);
    this.storage.removeItem(this.expiresKey);
  }

  /**
   * Panel open/closed flag (sessionStorage). Persists across page navigations
   * and refreshes within the tab so the chat window stays open as the visitor
   * browses the shop; a fresh tab starts closed.
   */
  isPanelOpen(): boolean {
    if (!this.sessionStore) return false;
    return this.sessionStore.getItem(this.panelOpenKey) === '1';
  }

  setPanelOpen(open: boolean): void {
    if (!this.sessionStore) return;
    this.sessionStore.setItem(this.panelOpenKey, open ? '1' : '0');
  }

  /**
   * Mobile auto-peek dismissal flag (sessionStorage) — once dismissed, the peek
   * does not re-open for the rest of the tab session.
   */
  isPeekDismissed(): boolean {
    if (!this.sessionStore) return false;
    return this.sessionStore.getItem(this.peekDismissedKey) === '1';
  }

  markPeekDismissed(): void {
    if (!this.sessionStore) return;
    this.sessionStore.setItem(this.peekDismissedKey, '1');
  }

  /**
   * Host nudge dismissal (sessionStorage, per nudge id) — closed (X) or clicked
   * bubbles stay hidden for the rest of the tab session; a new tab starts clean.
   */
  isNudgeDismissed(id: string): boolean {
    if (!this.sessionStore) return false;
    return this.sessionStore.getItem(this.nudgeDismissedPrefix + id) === '1';
  }

  markNudgeDismissed(id: string): void {
    if (!this.sessionStore) return;
    this.sessionStore.setItem(this.nudgeDismissedPrefix + id, '1');
  }
}
